import React, { useEffect, useState, Fragment } from "react";
import {
  URL_PARTICIPANTS_SHIFT_NOTES,
  URL_PARTICIPANTS_SHIFT_NOTES_STANDARD_USER,
  URL_EXPORT_SHIFT_NOTES_CSV,
  URL_EXPORT_SHIFT_NOTES_PDF,
  URL_EXPORT_SHIFT_NOTES_CSV_STANDARD_USER,
  URL_EXPORT_SHIFT_NOTES_PDF_STANDARD_USER,
} from "store/services/URL";
import { apiPostRequest } from "helpers/Requests";
import { useParams } from "react-router-dom";
import PaginationComponent from "components/common/Pagination/index";
import { emptyPaginationObj, USER_ROLE } from "Constants/constant";
import CircularLoader from "../../../common/CircularLoader/CircularLoader";
import {
  addMonths,
  baseURLImage,
  getNameProfile,
  downloadFileFromServer,
} from "../../../../Utils/utils";
import DateRangePickerNew from "../../../common/DateRangePicker/DateRangePickerNew";
import SelectOption from "components/common/Select/Select";
import { roleAccess } from "../../../../helpers/RolesPermission";
import {
  BLK_PORTAL_PREMISSION_KEYS_CHILDREN,
  PERMISSION_KEYS,
} from "../../../../store/constants";
import { Menu, Transition } from "@headlessui/react";
import { HiOutlineDocumentDownload, HiOutlineDocumentText, HiChevronDown } from "react-icons/hi";

import moment from 'moment';
import {toast} from "react-toastify";

const ShiftNotes = ({participantInfo}) => {

  // for api notes
  const [shiftNotes, setShiftNotes] = useState([]);
  // for date range
  const [startDate, setStartDate] = useState(() => {
   const today = new Date();
   today.setDate(today.getDate() - 90);
   return today;
  });
  const [endDate, setEndDate] = useState(new Date());
  // state use for validating user not to select date range beyond 3 months.
  const [selectedFromDate, setSelectedFromDate] = useState(null);

  // pagination
  const [pagination, setPagination] = useState(emptyPaginationObj);
  const [page, setPage] = useState(1);

  // loading state
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // for setting and selecting the worker
  const [selectedWorker, setSelectedWorker] = useState({
    label: "All",
    value: "all",
  });

  const [workerList, setWorkerList] = useState();

  // take uuid of the participants
  const { uuid } = useParams();

  const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return "Not available";
    return {
      date: moment(dateTimeStr).format("DD/MM/YYYY hh:mm A"),
    };
  };

  const createWorkerList = (workersData) => {
    let workers = [{ label: "All", value: "all" }];
    let addedWorkers = {};

    workersData.forEach((worker) => {
      // value must be employee profile id — matches timesheet.worker_id and export API filter
      if (!addedWorkers[worker.id]) {
        workers.push({
          label: `${worker.first_name} ${worker.last_name}`,
          value: worker.id,
        });
        addedWorkers[worker.id] = true;
      }
    });

    workers.sort((a, b) => {
      if (a.value === "all") return -1;
      if (b.value === "all") return 1;
      return a.label.localeCompare(b.label);
    });

    return workers;
  };


  const getShiftNotes = async () => {
    // don't call any API when start date and end date is null
    if (!startDate || !endDate) return;
    setIsLoading(true);

    try {
      const isSpUser = roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager]);
      const endpoint = !isSpUser
          ? `${URL_PARTICIPANTS_SHIFT_NOTES_STANDARD_USER}`
          : `${URL_PARTICIPANTS_SHIFT_NOTES}`;

      // format to fetch data form API
      const formattedStartDate = moment(startDate).format('YYYY-MM-DD');
      const formattedEndDate = moment(endDate).format('YYYY-MM-DD');

      const payload = {
        uuid,
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        page: page,
        worker_id: selectedWorker?.value === "all"
            ? null
            : parseInt(selectedWorker?.value),
      };

      const response = await apiPostRequest(endpoint, payload);

      if (response.status === 200) {

        const notes = response.data.data.shift_notes;
        const paginationData = response.data.data.pagination;

        setShiftNotes(notes);
        setPagination(paginationData);
      }
    } catch (error) {
      toast.error("Error fetching shift notes",error);

    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (participantInfo?.sp_workers?.length > 0) {
      const workersList = createWorkerList(participantInfo.sp_workers);
      setWorkerList(workersList);
    }
  }, [participantInfo?.sp_workers]);

  // Fetch notes when page, dates change, or worker is selected
  useEffect(() => {
    getShiftNotes();
  }, [page, startDate, endDate, selectedWorker]);

  // Event handlers
  const handleDateChange = (dates) => {
    if (dates?.fromDate && dates?.toDate) {
      setStartDate(dates.fromDate);
      setEndDate(dates.toDate);
    } else {
      setStartDate(null);
      setEndDate(null);
    }
  };

  const handleCancel = () => {
    setStartDate(null);
    setEndDate(null);
    setShiftNotes([])
    setPage(1);
  };

  const handleExport = async (exportType) => {
    // Validate dates
    if (!startDate || !endDate) {
      toast.error("Please select a date range to export");
      return;
    }

    setIsExporting(true);
    try {
      const isSpUser = roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager]);
      const endpoint = exportType === 'CSV'
        ? (!isSpUser ? URL_EXPORT_SHIFT_NOTES_CSV_STANDARD_USER : URL_EXPORT_SHIFT_NOTES_CSV)
        : (!isSpUser ? URL_EXPORT_SHIFT_NOTES_PDF_STANDARD_USER : URL_EXPORT_SHIFT_NOTES_PDF);

      const formattedStartDate = moment(startDate).format('YYYY-MM-DD');
      const formattedEndDate = moment(endDate).format('YYYY-MM-DD');

      const payload = {
        uuid,
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        worker_id: selectedWorker?.value === "all" ? null : parseInt(selectedWorker?.value),
      };

      const response = await apiPostRequest(endpoint, payload);

      if (response.status === 200) {
        const downloadUrl = response.data.data;
        const fileName = `shift_notes_${moment().format('YYYY-MM-DD_HHmmss')}.${exportType.toLowerCase()}`;
        
        // Download file using fetch and blob (handles cross-origin properly)
        try {
          const fileResponse = await fetch(downloadUrl);
          if (!fileResponse.ok) {
            throw new Error('Failed to download file');
          }
          const blob = await fileResponse.blob();
          
          // Create download link
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } catch (downloadError) {
          console.error('Download error:', downloadError);
          // Fallback: open in new tab if download fails
          window.open(downloadUrl, "_blank");
        }
        
        toast.success(`${exportType} export generated successfully`);
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || `Error exporting ${exportType}`;
      toast.error(errorMessage);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <div>
        {(roleAccess(
          [USER_ROLE.Sp_Standard_User],
          PERMISSION_KEYS.PARTICIPANT,
          BLK_PORTAL_PREMISSION_KEYS_CHILDREN.PARTICIPANT.CASE_NOTES.STORE
        ) ||
          roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager])) && (
          <div className="px-4 sm:px-6 py-4 sm:py-6">
            {/* Filter Section */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm mb-6 ">
              <div className="bg-gradient-to-r from-gray-50 to-blue-50 border-b border-gray-200 px-4 sm:px-6 py-4 flex justify-between items-center">
                <h3 className="text-sm font-semibold text-gray-700">Filters</h3>
                <div className="flex gap-2">
                  <Menu as="div" className="relative inline-block text-left">
                    <div>
                      <Menu.Button
                        className="inline-flex items-center justify-center w-full rounded-md border border-blue-600/20 shadow-sm px-4 py-2 bg-gradient text-sm font-medium text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        disabled={isExporting || !startDate || !endDate}
                      >
                        {isExporting ? (
                          <>
                            <CircularLoader
                              classes="shrink-0 mr-2"
                              width_size="w-6 h-6 object-contain"
                            />
                            Exporting...
                          </>
                        ) : (
                          <>
                            <HiOutlineDocumentDownload className="h-4 w-4 mr-2" />
                            Export
                            <HiChevronDown className="ml-2 h-4 w-4" />
                          </>
                        )}
                      </Menu.Button>
                    </div>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                        <div className="py-1">
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                onClick={() => handleExport('CSV')}
                                className={`${
                                  active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                                } group flex items-center w-full px-4 py-2 text-sm`}
                                disabled={isExporting}
                              >
                                <HiOutlineDocumentDownload className="mr-3 h-4 w-4 text-gray-400 group-hover:text-gray-500" />
                                Export as CSV
                              </button>
                            )}
                          </Menu.Item>
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                onClick={() => handleExport('PDF')}
                                className={`${
                                  active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                                } group flex items-center w-full px-4 py-2 text-sm`}
                                disabled={isExporting}
                              >
                                <HiOutlineDocumentText className="mr-3 h-4 w-4 text-gray-400 group-hover:text-gray-500" />
                                Export as PDF
                              </button>
                            )}
                          </Menu.Item>
                        </div>
                      </Menu.Items>
                    </Transition>
                  </Menu>
                </div>
              </div>
              <div className="p-4 sm:p-6">
                <div className="flex flex-col lg:flex-row lg:items-end gap-4 lg:gap-6">
                  <div className="flex-1">
                    <label
                        htmlFor="dateRange"
                        className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Filter By Shift Date <span className="text-xs text-gray-500 font-normal">(Max 3 Months)</span>
                    </label>
                    <DateRangePickerNew
                        fromDate={startDate}
                        toDate={endDate}
                        handleChange={handleDateChange}
                        handleCancel={handleCancel}
                        setSelectedFromDate={setSelectedFromDate}
                        maxDate={addMonths(selectedFromDate, 3)}
                    />
                  </div>

                  <div className="w-full lg:w-64">
                    <label
                      htmlFor="worker"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Filter By Worker
                    </label>
                    <SelectOption
                      name="worker_id"
                      isSearch={true}
                      data={workerList}
                      selectedOption={selectedWorker}
                      handleChange={(e) => {
                        setSelectedWorker(e);
                        setPage(1);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Display notes */}
            <div>
              {isLoading ? (
                <div className="flex justify-center items-center w-full bg-white border border-gray-200 rounded-xl shadow-sm py-16">
                  <CircularLoader
                    classes="flex justify-center items-center"
                    width_size="w-24 h-24 object-contain"
                  />
                </div>
              ) : shiftNotes.length > 0 ? (
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                  <div className="max-h-[600px] overflow-y-auto custom-scroll">
                    {shiftNotes.map((note, index) => (
                      <div
                        className={`border-b border-gray-100 last:border-b-0 transition-colors hover:bg-blue-50/50 ${
                          index % 2 ? "bg-white" : "bg-gray-50/50"
                        }`}
                        key={index}
                      >
                        <div className="flex gap-4 py-5 px-4 sm:px-6">
                          <div className="flex-shrink-0">
                            <img
                              alt="avatar"
                              className="h-12 w-12 rounded-full object-cover ring-2 ring-gray-200 shadow-sm"
                              src={
                                note.avatar
                                  ? baseURLImage(note.avatar)
                                  : getNameProfile(
                                      note?.worker_first_name || " ",
                                      note?.worker_last_name
                                    )
                              }
                            />
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-3">
                              <div>
                                <strong className="text-base font-semibold text-gray-900">
                                  {note.worker_first_name || "N/A"}{" "}
                                  {note.worker_last_name || "N/A"}
                                </strong>
                              </div>
                              <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-md inline-block">
                                {formatDateTime(note.worker_start_time).date} - {formatDateTime(note.worker_end_time).date}
                              </div>
                            </div>

                            <div className="text-sm text-gray-700 leading-relaxed">
                              <p className="whitespace-pre-wrap break-words">
                                {(
                                  note.worker_notes || "No notes available"
                                ).replace(/^\w/, (c) => c.toUpperCase())}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="py-16 flex justify-center items-center bg-white border border-gray-200 rounded-xl shadow-sm">
                  <div className="flex flex-col justify-center items-center space-y-4">
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div className="text-center">
                      <p className="font-semibold text-gray-600 text-base">No Notes Found!</p>
                      <p className="text-sm text-gray-500 mt-1">Try adjusting your filters to see more results</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Pagination */}
            <div className="mt-6">
              {shiftNotes.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm px-4 sm:px-6 py-4">
                  <PaginationComponent
                    page={pagination.current_page}
                    activeListLength={shiftNotes}
                    limit={10}
                    totalList={pagination.total}
                    handlePagination={(newPage) => {
                      setPage(newPage);
                    }}
                    totalPages={pagination.last_page}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ShiftNotes;
